import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CloverOrderService} from '../../../_services/cloverOrder.service';
import {User} from '../../../_models';
import {AESEncryptDecryptService} from '../../../_services/aesencrypt-decrypt.service';
import {CartService} from '../../../_services/cart.service';
import {Observable} from "rxjs";
import {takeWhile} from 'rxjs/operators';
import {CloverApiService} from "../../../_services/cloverApi.service";
import { Constants } from 'src/app/constants/constants';
import {UtilsService} from '../../../_services/utils.service';


@Component({
  selector: 'app-notification-popup',
  templateUrl: './notification-and-image-popup.component.html',
  styleUrls: ['./notification-and-image-popup.component.scss']
})
export class NotificationAndImagePopupComponent implements OnInit {
  readonly Constants = Constants;
  description: string;
  message: string;
  imgSrc: string;
  orderId: string;
  deviceName: string;
  // Optional device override passed via dialog data. The cashier-register
  // lets the cashier pick a device per session that may differ from
  // currentUser.takePayment, so the cancel signal must target the picked
  // device — not the user's default. Falls back to currentUser.takePayment
  // for legacy callers (kiosk, view-cart, paymentUser).
  dialogDeviceId: string | undefined;
  dialogDeviceSerial: string | undefined;
  isNewOrder: boolean;
  // Split-tender progress context — present only during split payment flows.
  splitProgress: { current: number; total: number; amountCents: number } | null = null;
  currentUser: User;
  retryPaymentDisabled = true;
  time: number;
  orderTotal: number;
  selectedRole: string;

  constructor(private dialogRef: MatDialogRef<NotificationAndImagePopupComponent>, private cloverOrderService: CloverOrderService,
              private cloverApiService: CloverApiService, private utilsService: UtilsService, private _AESEncryptDecryptService: AESEncryptDecryptService, private cartService: CartService, @Inject(MAT_DIALOG_DATA) data) {
    this.description = data.title;
    this.message = data.message;
    this.imgSrc = data.imgSrc;
    this.deviceName = data.deviceName || '';
    this.orderId = data.orderId;
    this.orderTotal = data.orderTotal? data.orderTotal : 0;
    this.dialogDeviceId = data.deviceId;
    this.dialogDeviceSerial = data.deviceSerial;
    this.isNewOrder = !!data.isNewOrder;
    this.splitProgress = data.splitProgress || null;
    this.currentUser = utilsService.getCurrentUser();
    this.selectedRole = this.currentUser?.selectedRole;
  }

  ngOnInit() {
    this.disableRetryButton(21);
  }

  private isStaffRole(): boolean {
    // Admin/Manager/Server are logged-in staff operating a manned register,
    // not an unattended Kiosk/Table/PaymentsDisplay device. Same triad used
    // in cashier-register.component.ts (e.g. lines 5964, 7129-7131).
    return this.selectedRole === Constants.ADMIN
      || this.selectedRole === Constants.MANAGER
      || this.selectedRole === Constants.SERVER;
  }

  private disableRetryButton(seconds) {
    if (this.isStaffRole()) {
      // Staff resend immediately — skip the 20s device-warm-up countdown that
      // unattended devices (Kiosk/Table/PaymentsDisplay) keep. Retain only a
      // short re-entrancy guard so a nervous double-tap can't fire two
      // payOrderOnDevice dispatches back-to-back.
      this.time = 0;
      setTimeout(() => {
        this.retryPaymentDisabled = false;
      }, 1500);
      return;
    }
    this.time = seconds;
    const timer = Observable.timer(0, 1000);
    timer.pipe(takeWhile(val => this.time > 0)).subscribe(t => {
      this.time--;
    });
    setTimeout(() => {
      this.retryPaymentDisabled = false;
    }, (seconds - 1) * 1000);
  }

  closeAndCancelOrder() {
    const deviceId = this.dialogDeviceId || this.currentUser?.takePayment?.cloverDeviceId;
    const deviceSerial = this.dialogDeviceSerial || this.currentUser?.takePayment?.cloverDeviceSerial;

    if (this.selectedRole === Constants.KIOSK) {
      // Kiosk: order was created on the fly, so delete it AND reset the device
      // (the existing OpenKiosk path already aborts the payment screen).
      this.cloverOrderService.deleteOrder(this.orderId).subscribe(() => {
        this.cloverApiService.initKioskOnDevice(deviceId, deviceSerial).subscribe(() => {});
        // Signal an explicit user cancel so the cashier-register afterClosed
        // handler runs its cancel-cleanup. Programmatic completion closes
        // (dialog.closeAll from the payment-completed path) pass no result and
        // must NOT be treated as a cancel — that misread reset split-tender
        // state mid-flow and killed the final completion.
        this.dialogRef.close({ cancelled: true });
      });
      return;
    }

    // Bug 3: cashier-side cancel previously only closed the dialog locally,
    // leaving the connected Clover Mini stuck on the payment screen and
    // unable to take the next transaction. Send an explicit CancelPayment
    // event to the device so it returns to its idle screen.
    if (deviceId && this.orderId) {
      this.cloverOrderService.cancelPaymentOnDevice(this.orderId, deviceId, deviceSerial).subscribe({
        next: () => {
          if (this.isNewOrder) {
            this.cloverOrderService.deleteOrder(this.orderId).subscribe(() => {}, () => {});
          }
          this.dialogRef.close({ cancelled: true });
        },
        // Don't strand the cashier if the cancel signal fails — close
        // the local dialog regardless and surface the device-stuck case
        // through the existing notification system.
        error: () => this.dialogRef.close({ cancelled: true })
      });
    } else {
      this.dialogRef.close({ cancelled: true });
    }
  }

  resendPaymentToDevice() {
    this.retryPaymentDisabled = true;
    this.disableRetryButton(21);
    // this.resendingPaymentToDeviceAfterDelay(11);
    if(this.selectedRole === 'PaymentsDisplay'){
      const pdDeviceId = this.dialogDeviceId || this.currentUser?.takePayment?.cloverDeviceId;
      const pdDeviceSerial = this.dialogDeviceSerial || this.currentUser?.takePayment?.cloverDeviceSerial;
      this.cloverOrderService.getOrder(this.orderId).subscribe(order => {
        this.cloverOrderService.payOrderOnDevice(order.id, order.total,
          pdDeviceId, pdDeviceSerial, true)
          .subscribe(response => {
          });
      });
    } else {
      // Prefer the dialog-provided orderTotal (the frontend total the cashier
      // saw on screen, with the tax-checkbox intent baked in). Falls back to
      // cartService.cloverOrder.total only when the caller did not pass one
      // — that legacy fallback is the Clover-server-side tax-inclusive total
      // and would re-quote the device with tax even after the cashier
      // toggled it off. The cashier-register popup always passes orderTotal,
      // so this fallback only fires on legacy callers.
      const total = (this.orderTotal && this.orderTotal > 0)
        ? this.orderTotal
        : (this.cartService?.cloverOrder?.total || 0);
      const deviceId = this.dialogDeviceId || this.currentUser?.takePayment?.cloverDeviceId;
      const deviceSerial = this.dialogDeviceSerial || this.currentUser?.takePayment?.cloverDeviceSerial;
      this.cloverOrderService.payOrderOnDevice(this.orderId, total,
        deviceId, deviceSerial, true)
        .subscribe(response => {
        });
    }
  }

  getTimerValue() {
    if (this.time > 0) {
      return 'Resend Payment to Device ' +this.time.toString();
    } else {
      return 'Resend Payment to Device';

    }
  }
}
